"use client";
import { useEffect, useRef, type RefObject } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  sharePostImage,
  type PostImageShareTarget,
} from "./post-image-transfer";
type PostImageShareStatus =
  | "idle"
  | "copying"
  | "copied"
  | "downloading"
  | "downloaded";
type PostImageShare = {
  readonly status: PostImageShareStatus;
  readonly error: string | null;
  readonly share: (target: PostImageShareTarget) => void;
  readonly dismissError: () => void;
};

export function usePostImageShare({
  cardRef,
}: {
  readonly cardRef: RefObject<HTMLElement | null>;
}): PostImageShare {
  const busy = useRef(false);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const mutation = useMutation({
    mutationFn: (target: PostImageShareTarget) => {
      const card = cardRef.current;

      if (!card) throw new Error("Post image is not mounted.");

      return sharePostImage({ card, target });
    },
    onSettled: () => {
      busy.current = false;
    },
  });
  const { isSuccess, reset } = mutation;
  useEffect(() => {
    if (!isSuccess) return;

    feedbackTimer.current = setTimeout(reset, 2000);
    return () => clearTimeout(feedbackTimer.current);
  }, [isSuccess, reset]);

  let status: PostImageShareStatus = "idle";

  if (mutation.isPending) {
    status = mutation.variables === "copy-image" ? "copying" : "downloading";
  } else if (mutation.isSuccess) {
    status = mutation.variables === "copy-image" ? "copied" : "downloaded";
  }

  let error: string | null = null;

  if (mutation.isError) {
    error =
      mutation.variables === "copy-image"
        ? "Couldn't copy this post image. Please try again."
        : "Couldn't download this post image. Please try again.";
  }

  return {
    status,
    error,
    share: (target) => {
      if (busy.current) return;

      clearTimeout(feedbackTimer.current);
      busy.current = true;
      mutation.mutate(target);
    },
    dismissError: () => {
      if (mutation.isError) reset();
    },
  };
}
